import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, LayoutDashboard, KeyRound, UserRound, MessageCircle, CreditCard, LifeBuoy, History, Settings2 } from 'lucide-react'
import { platformAPI, tenantDataAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { loadTenantDirectory, invalidateTenantDirectory } from '../lib/tenantDirectory'
import { toTenantModel, tenantView, isMissingEndpoint } from '../lib/support'
import { Spinner, ErrorBanner } from '../components/ui'
import { TenantRail } from '../components/tenant/TenantRail'
import { OverviewTab } from '../components/tenant/OverviewTab'
import { AccessTab } from '../components/tenant/AccessTab'
import { ClientsTab } from '../components/tenant/ClientsTab'
import { WhatsappTab } from '../components/tenant/WhatsappTab'
import { BillingTab } from '../components/tenant/BillingTab'
import { ChatsTab } from '../components/tenant/ChatsTab'
import { ActivityTab, buildDerivedActivity } from '../components/tenant/ActivityTab'
import { ConfigTab } from '../components/tenant/ConfigTab'
import { PurgeTenantModal } from '../components/PurgeTenantModal'

// Tenant workspace: sticky summary rail + tabs (?tab=). Staff see every tab except
// Clients and Studio setup; the API enforces the same split.
const TABS = [
  { key: 'overview', label: 'Visão geral', icon: LayoutDashboard },
  { key: 'access', label: 'Acesso', icon: KeyRound },
  { key: 'clients', label: 'Clientes', icon: UserRound, ownerOnly: true },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'billing', label: 'Plano e cobrança', icon: CreditCard },
  { key: 'chats', label: 'Conversas de suporte', icon: LifeBuoy },
  { key: 'activity', label: 'Atividade', icon: History },
  { key: 'config', label: 'Configuração do estúdio', icon: Settings2, ownerOnly: true },
]

const WA_ACTION_LABEL = { stop: 'Parar', restart: 'Reiniciar', logout: 'Desconectar', delete: 'Excluir sessão' }

const missingEndpoints = new Set()

const errorMessage = (err) => err?.response?.data?.error?.message || err?.response?.data?.error || err?.message

export const TenantDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isOwner } = useAuth()

  const [tenant, setTenant] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [staff, setStaff] = useState([])
  const [staffLoading, setStaffLoading] = useState(false)
  const [state, setState] = useState(null)
  const [stateLoading, setStateLoading] = useState(false)
  const [stateError, setStateError] = useState(false)
  const [chats, setChats] = useState([])
  const [chatsLoading, setChatsLoading] = useState(false)
  const [notes, setNotes] = useState({ list: [], unavailable: false })
  const [remoteActivity, setRemoteActivity] = useState(null)
  const [failed, setFailed] = useState({ list: null, unavailable: false })
  const [waAction, setWaAction] = useState(null)
  const [sendingReset, setSendingReset] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const [purgeOpen, setPurgeOpen] = useState(false)

  const visibleTabs = TABS.filter((t) => isOwner || !t.ownerOnly)
  const requested = searchParams.get('tab')
  const tab = visibleTabs.some((t) => t.key === requested) ? requested : 'overview'
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams)
    if (!key || key === 'overview') next.delete('tab')
    else next.set('tab', key)
    if (key !== 'clients') {
      next.delete('q')
      next.delete('deleted')
    }
    setSearchParams(next)
  }

  const loadStaff = useCallback(async () => {
    setStaffLoading(true)
    try {
      const res = await tenantDataAPI.getTenantUsers(id, { per_page: 100 })
      setStaff(res.data.data || [])
    } catch {
      setStaff([])
    } finally {
      setStaffLoading(false)
    }
  }, [id])

  const loadState = useCallback(async () => {
    setStateLoading(true)
    setStateError(false)
    try {
      const res = await tenantDataAPI.getTenantState(id)
      setState(res.data.data || null)
    } catch {
      setState(null)
      setStateError(true)
    } finally {
      setStateLoading(false)
    }
  }, [id])

  // Optional support-console endpoints: a 404/405 means "not deployed yet". Remember
  // that for the session so every workspace visit doesn't re-request them.
  const loadOptional = useCallback(async () => {
    const optional = (key, request, onData, onError) => {
      if (missingEndpoints.has(key)) return onError(true)
      request()
        .then((res) => onData(res.data.data || []))
        .catch((err) => {
          const missing = isMissingEndpoint(err)
          if (missing) missingEndpoints.add(key)
          onError(missing)
        })
    }
    optional(
      'notes',
      () => tenantDataAPI.getTenantNotes(id),
      (list) => setNotes({ list, unavailable: false }),
      (missing) => setNotes({ list: [], unavailable: missing })
    )
    optional(
      'activity',
      () => tenantDataAPI.getTenantActivity(id),
      (list) => setRemoteActivity(list),
      () => setRemoteActivity(null)
    )
    optional(
      'failed',
      () => tenantDataAPI.getFailedMessages(id),
      (list) => setFailed({ list, unavailable: false }),
      (missing) => setFailed({ list: missing ? null : [], unavailable: missing })
    )
  }, [id])

  const loadTenant = useCallback(async () => {
    // The detail endpoint has no billing_state / trial / program fields; the list row
    // does, so merge it in from the shared directory.
    const [detail, directory] = await Promise.all([
      platformAPI.getTenant(id),
      loadTenantDirectory().catch(() => []),
    ])
    const data = detail.data.data || detail.data
    const row = directory.find((t) => t.id === id) || {}
    setTenant({ ...row, ...data, statistics: { ...row.statistics, ...data.statistics } })
  }, [id])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError('')
    setActionError('')
    loadTenant()
      .catch((err) => {
        if (cancelled) return
        setLoadError(err.response?.status === 404 ? 'Estúdio não encontrado.' : `Falha ao carregar o estúdio: ${errorMessage(err)}`)
      })
      .finally(() => !cancelled && setLoading(false))
    loadStaff()
    loadState()
    loadOptional()
    setChatsLoading(true)
    tenantDataAPI
      .getTenantSupportChats(id)
      .then((res) => !cancelled && setChats(res.data.data || []))
      .catch(() => !cancelled && setChats([]))
      .finally(() => !cancelled && setChatsLoading(false))
    return () => {
      cancelled = true
    }
  }, [id, loadTenant, loadStaff, loadState, loadOptional])

  const lastActive = useMemo(() => {
    const times = staff.map((u) => u.last_login_at).filter(Boolean).map((t) => new Date(t).getTime())
    return times.length ? new Date(Math.max(...times)).toISOString() : null
  }, [staff])

  const view = useMemo(
    () =>
      tenant
        ? tenantView(
            toTenantModel(tenant, {
              staff: staffLoading && staff.length === 0 ? undefined : staff,
              state: state?.whatsapp,
              lastActive,
            })
          )
        : null,
    [tenant, staff, staffLoading, state, lastActive]
  )

  const activity = useMemo(
    () =>
      remoteActivity
        ? { events: remoteActivity, derived: false }
        : { events: buildDerivedActivity(tenant, staff, chats), derived: true },
    [remoteActivity, tenant, staff, chats]
  )

  const refreshTenant = async () => {
    invalidateTenantDirectory()
    try {
      await loadTenant()
    } catch (err) {
      setActionError(`Falha ao atualizar o estúdio: ${errorMessage(err)}`)
    }
  }

  const handleToggleStatus = async () => {
    const suspending = view.model.active
    if (suspending && !window.confirm(`Suspender ${view.model.name}? Ninguém neste estúdio conseguirá entrar.`)) return
    setStatusBusy(true)
    setActionError('')
    try {
      if (suspending) await platformAPI.suspendTenant(id)
      else await platformAPI.activateTenant(id)
      await refreshTenant()
      loadStaff()
    } catch {
      setActionError(`Falha ao ${suspending ? 'suspender' : 'ativar'} o estúdio. Tente novamente.`)
    } finally {
      setStatusBusy(false)
    }
  }

  const handleSendOwnerResetLink = async () => {
    setSendingReset(true)
    setActionError('')
    try {
      const response = await platformAPI.sendOwnerResetLink(id)
      const resetLink = response?.data?.data?.reset_link || response?.data?.reset_link
      alert(`Link de redefinição do proprietário gerado e enviado por e-mail/registrado.\n\n${resetLink}`)
    } catch (err) {
      setActionError(`Falha ao enviar o link de redefinição: ${errorMessage(err)}`)
    } finally {
      setSendingReset(false)
    }
  }

  const handleWhatsappAction = async (action) => {
    if (action === 'delete' && !window.confirm('Isso excluirá permanentemente a sessão do WhatsApp. Tem certeza?')) return
    if (
      action === 'logout' &&
      !window.confirm('Isso desvinculará a conta do WhatsApp. O estúdio precisará escanear o QR code novamente. Continuar?')
    )
      return
    setWaAction(action)
    setActionError('')
    try {
      const fn = {
        stop: tenantDataAPI.stopWhatsappSession,
        restart: tenantDataAPI.restartWhatsappSession,
        logout: tenantDataAPI.logoutWhatsappSession,
        delete: tenantDataAPI.deleteWhatsappSession,
      }[action]
      await fn(id)
      await loadState()
    } catch (err) {
      setActionError(`Falha na ação “${WA_ACTION_LABEL[action] || action}” do WhatsApp: ${errorMessage(err)}`)
    } finally {
      setWaAction(null)
    }
  }

  const handleAddNote = async (text) => {
    const res = await tenantDataAPI.addTenantNote(id, text)
    const note = res.data.data
    setNotes((n) => ({ ...n, list: note ? [note, ...n.list] : n.list }))
  }

  if (loading && !tenant) return <Spinner className="h-64" />

  if (loadError || !view) {
    return (
      <div className="flex flex-col gap-4">
        <button type="button" className="btn btn-ghost self-start" onClick={() => navigate('/tenants')}>
          <ArrowLeft className="h-4 w-4" />
          Estúdios
        </button>
        <ErrorBanner>{loadError || 'Estúdio não encontrado.'}</ErrorBanner>
      </div>
    )
  }

  const openIssues = view.issues.length

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
      <TenantRail
        view={view}
        onTab={setTab}
        onToggleStatus={handleToggleStatus}
        statusBusy={statusBusy}
        onSendReset={handleSendOwnerResetLink}
        sendingReset={sendingReset}
        onPurge={() => setPurgeOpen(true)}
      />

      <div className="flex min-w-0 flex-col gap-4">
        <div role="tablist" aria-label="Seções do estúdio" className="card flex flex-wrap gap-1 self-start p-1">
          {visibleTabs.map((t) => {
            const Icon = t.icon
            const selected = tab === t.key
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-3.5 py-[7px] text-[13px] transition-colors ${
                  selected ? 'bg-admin-50 text-primary-800' : 'text-admin-900 hover:bg-admin-50'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {t.key === 'overview' && openIssues > 0 && (
                  <span className="rounded-md bg-red-600 px-1.5 text-[11px] text-white" aria-label={`${openIssues} pendências em aberto`}>
                    {openIssues}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

        {tab === 'overview' && (
          <OverviewTab view={view} onTab={setTab} notes={notes} onAddNote={handleAddNote} activity={activity} />
        )}
        {tab === 'access' && <AccessTab staff={staff} loading={staffLoading} onRefresh={loadStaff} />}
        {tab === 'clients' && (
          <ClientsTab
            tenantId={id}
            initialQuery={searchParams.get('q') || ''}
            initialIncludeDeleted={searchParams.get('deleted') === '1'}
          />
        )}
        {tab === 'whatsapp' && (
          <WhatsappTab
            view={view}
            loading={stateLoading}
            stateError={stateError}
            onAction={handleWhatsappAction}
            busyAction={waAction}
            failed={failed}
          />
        )}
        {tab === 'billing' && (
          <BillingTab
            view={view}
            chatCount={chats.filter((c) => Date.now() - new Date(c.created_at) < 30 * 86400000).length}
            onChange={refreshTenant}
          />
        )}
        {tab === 'chats' && <ChatsTab chats={chats} loading={chatsLoading} />}
        {tab === 'activity' && <ActivityTab activity={activity} />}
        {tab === 'config' && <ConfigTab tenantId={id} state={state} loading={stateLoading} />}
      </div>

      {purgeOpen && (
        <PurgeTenantModal
          tenant={tenant}
          onClose={() => setPurgeOpen(false)}
          onPurged={() => {
            invalidateTenantDirectory()
            navigate('/tenants')
          }}
        />
      )}
    </div>
  )
}
